import { StorageEngine } from '@ngxs/storage-plugin';

/**
 * The storage engine of `auth.refreshToken`: a tab writes the shared session only when its own token changed (website-web#59, QA doc
 * features/website/login-second-tab-2026-10-13.md R-WLT2, R-WLT6).
 *
 * The storage plugin calls `setItem` for every persisted key after EVERY action of EVERY tab, so a tab re-wrote whatever its state still
 * held over what another tab had stored since:
 * - a signed-out tab (an old tab, a second Log In tab, one reloading to follow a sign-in, one whose sign-in was refused) wrote its empty
 *   token over a fresh sign-in, and the signed-in tab took that for "signed out elsewhere" (R-SAL29) and ended its new session;
 * - a signed-in tab that had not yet seen another tab's renewal wrote its old token back over the new one, and the tabs then followed a
 *   token the server had already deleted (R-SAL26).
 * Writing the stored value back is not enough: each tab reads localStorage from its own copy, which learns of another tab's write a
 * moment later, so "writing back what is stored" could still put the old token back (seen with four tabs, 13.10.2026).
 *
 * So the write is skipped when the value is the one this tab last loaded or wrote (nothing changed here), or when it is already what is
 * stored (a token adopted from another tab). A changed value - a sign-in, a renewal, Log Out, an expiry, a refused renewal - is written,
 * so a Log Out or an expiry still ends every tab.
 *
 * One engine per page (per tab); `storage` is read lazily because it can be missing or throw when blocked.
 */
export const createAuthRefreshTokenStorageEngine = (
  storage: () => Pick<Storage, 'getItem' | 'setItem'> | undefined
): StorageEngine => {
  /** The auth state starts with an empty token, which the plugin serializes as `""`. */
  const empty = JSON.stringify('');
  let lastOwn: string = empty;

  const read = (key: string): string | null => {
    try {
      return storage()?.getItem(key) ?? null;
    } catch {
      return null;
    }
  };

  return {
    getItem: (key: string): string | null => {
      const raw = read(key);
      lastOwn = raw ?? empty;
      return raw;
    },
    setItem: (key: string, value: string): void => {
      if (value === lastOwn) return;
      lastOwn = value;
      if (value === read(key)) return;
      try {
        storage()?.setItem(key, value);
      } catch {
        // storage full or blocked: the session lives on in this tab's memory only
      }
    }
  };
};
